/**
 * 404, 500
 * @description 404, 500 에러 공통 컴포넌트
 * @param
 * @author 2037688
 * @history
 *   - [2026-02-09] 초기 작성
 */
import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  BottomButtons,
  CarbonFree,
  ContentInner,
  ErrorCard,
  ErrorIconType404,
  ErrorIconType500,
  cn,
} from 'scbk-ui-web';

import { usePageMove, type LoginNextPageInfo } from 'scbk-shared-module';

import { Trans, useTranslation } from 'react-i18next';
import {
  ErrorPageComponent,
  ErrorPageComponentProps,
  EventType,
  deviceUtils,
  useEventBusEmit,
  useMenuRouter,
  useRouter,
} from 'scbk-services';
import _ from 'lodash';

/**
 * Content 내용이 String 배열일 경우 렌더링 처리
 * @param content
 * @returns
 */
const renderContent = (content?: string | string[] | React.ReactNode): React.ReactNode => {
  if (!content) return null;

  if (typeof content === 'string') {
    return content
      .split(/<br\s*\/?>/i)
      .filter((line) => line.trim() !== '')
      .map((line, index, arr) => (
        <React.Fragment key={index}>
          {line}
          {index < arr.length - 1 && <br />}
        </React.Fragment>
      ));
  }

  if (Array.isArray(content)) {
    return content
      .filter((line) => line.trim() !== '')
      .map((line, index, arr) => (
        <React.Fragment key={index}>
          {line}
          {index < arr.length - 1 && <br />}
        </React.Fragment>
      ));
  }

  return content;
};

export const ErrorComponent: ErrorPageComponent = React.memo((props: ErrorPageComponentProps) => {
  const { t } = useTranslation();

  const { goMain } = usePageMove(); // 메인이동 hook
  const { goMenu, replaceMenu } = useMenuRouter(); //menu router
  const router = useRouter(); // router
  const emit = useEventBusEmit(); // 이벤트 버스

  const isDesktop = useMemo(() => {
    return deviceUtils.isPC();
  }, []);

  /**
   * 에러코드 획득
   */
  const errorCode = useMemo(() => {
    return props.errorCode ?? '';
  }, [props.errorCode]);

  /**
   * 에러모듈 획득
   */
  const errorModule = useMemo(() => {
    return props.errorModule ?? '';
  }, [props.errorModule]);
  /**
   * 오류 메시지 획득
   */
  const errorMessage = useMemo(() => {
    return renderContent(props.errorMessage);
  }, [props.errorMessage]);

  const errClassName = useMemo(() => {
    return props.classNameProps ?? '';
  }, [props.classNameProps]);

  const desktopClassName = useMemo(() => {
    return props.desktopClassName ?? '';
  }, [props.desktopClassName]);

  const errButtons = useMemo(() => {
    return props.errButtons;
  }, [props.errButtons]);

  /**
   * 오류 가이드 메시지 획득
   */
  const errorGuideMessage = useMemo(() => {
    return props.errorGuideMessage ?? [];
  }, [props.errorGuideMessage]);

  /**
   * 하단 버튼 라벨 다국어 적용
   */
  const buttonText = useMemo(() => {
    const errorCode = props.errorCode;

    //기본
    let buttonText = t('ErrorComponent.002', {
      defaultValue: '확인',
    });

    //시간관련 에러코드인경우
    const timeErrorCodeArr = ['PRCCMM0027', 'PRCCMM0028', 'PRCCMM0054'];
    if (timeErrorCodeArr.includes(errorCode)) {
      buttonText = t('ErrorComponent.008', {
        defaultValue: '이용 가능한 시간 확인하기',
      });
    }

    return buttonText;
  }, [props.errorCode, t]);

  /**
   * 확인 버튼 클릭 시
   */
  const handleClickConfirm = useCallback(() => {
    props.reset?.();
    if (props.errorMenuId) {
      if (props?.errorMenuId === 'MAIN') {
        goMain();
        return;
      } else if (props?.errorMenuId === 'BACK') {
        router.back();
        return;
      }

      if (props.errorCode === 'PRCLGN0015') {
        const nextPageInfo: LoginNextPageInfo = {
          nextMenuId:
            props.errorMenuId == 'LGNADV000' || props.errorMenuId == 'LGNMTH000'
              ? ''
              : props.errorMenuId,
          nextPageMenuOptions: {
            routeData: { ...props?.errorMenuParameters },
          },
        };

        emit(EventType.LoginNavigationRequest, {
          routeData: { nextPageInfo: nextPageInfo },
        });
        return;
      }

      if (props.clearHistory) {
        goMenu(props.errorMenuId, {
          routeData: props?.errorMenuParameters ?? {},
          historyOptions: { clearHistory: true },
        });
        return;
      }

      replaceMenu(props.errorMenuId, {
        routeData: props?.errorMenuParameters ?? {},
      });
    } else if (props.onConfirm) {
      props.onConfirm?.();
    } else {
      goMain();
    }
  }, [goMain, props.onConfirm, props?.errorMenuId, props?.errorMenuParameters]);

  const NormalErrorComponent = ({
    type,
    classNameProps,
    desktopClassName,
    errButtonsProps,
  }: {
    type: 'Component' | 'Page';
    classNameProps?: string;
    desktopClassName?: string;
    errButtonsProps?: ReactNode | undefined;
  }) => {
    /**
     * className 확정
     */
    const className = useMemo(() => {
      return classNameProps
        ? classNameProps
        : type === 'Page'
          ? `flex flex-col items-center justify-center h-[calc(100dvh-244px)]`
          : `flex flex-col items-center justify-center h-[calc(100dvh_-_104px)]`;
    }, [type, classNameProps]);

    /**
     *
     */
    const desktopClass = useMemo(() => {
      // desktop css class
      let desktopClass = '';
      if (isDesktop && type === 'Page') {
        desktopClass = 'pt-10 h-fit';
      } else if (isDesktop && type === 'Component') {
        desktopClass = 'h-screen';
      }

      if (desktopClassName) {
        desktopClass = desktopClassName;
      }
      return desktopClass;
    }, [type, isDesktop, desktopClassName]);

    return (
      <>
        <div className={cn(className, desktopClass)}>
          <ErrorCard
            icon={type === 'Page' ? <CarbonFree /> : null}
            label={
              errorMessage ?? (
                // 서버접속이 원활하지 않습니다 <br /> 다시 접속해 주세요
                <Trans t={t} i18nKey="ErrorComponent.001" components={[<br key="0" />]} />
              )
            }
          >
            {/* <span className="text-state-disabled-04"></span> */}
            {errorGuideMessage.length > 0 &&
              type === 'Page' &&
              errorGuideMessage?.map((item, idx) => <p key={`${errorCode}_${idx}`}>{item}</p>)}
            {errorGuideMessage.length > 0 &&
              type === 'Component' &&
              errorGuideMessage?.map((item, idx) => (
                <React.Fragment key={`${errorCode}_${idx}`}>
                  <span className="text-state-disabled-04">{item}</span>
                  <br />
                </React.Fragment>
              ))}

            {type === 'Page' ? (
              <span className="block text-state-disabled-04 body-md mt-3">{`${errorCode}${!_.isEmpty(errorModule) ? ' - ' + errorModule : ''}`}</span>
            ) : (
              <span className="text-state-disabled-04">{`${errorCode}${!_.isEmpty(errorModule) ? ' - ' + errorModule : ''}`}</span>
            )}
          </ErrorCard>
        </div>
        {errButtonsProps ? (
          errButtonsProps
        ) : (
          <BottomButtons
            buttons={[
              {
                label: buttonText,
                onClick: () => {
                  handleClickConfirm();
                },
                Variants: 'primary',
                disabled: false,
              },
            ]}
          />
        )}
      </>
    );
  };

  const StatusErrorComponent = ({ type }: { type: 'Component' | 'Page' }) => {
    const className =
      type === 'Page'
        ? `flex flex-col items-center justify-center h-[calc(100dvh_-_104px)]`
        : `flex flex-col items-center justify-center h-screen`;

    // desktop css class
    let desktopClass = '';
    if (isDesktop && type === 'Page') {
      desktopClass = 'h-[500px]';
    } else if (isDesktop && type === 'Component') {
      desktopClass = 'h-screen';
    }

    return (
      <>
        <div className={cn(className, desktopClass)}>
          <ErrorCard
            icon={errorCode === '404' ? <ErrorIconType404 /> : <ErrorIconType500 />}
            label={
              errorCode === '404'
                ? t('ErrorComponent.003', {
                    defaultValue: '페이지를 찾을 수 없음',
                  })
                : t('ErrorComponent.004', {
                    defaultValue: '일시적인 서버 오류',
                  })
            }
          >
            {errorCode === '404' ? (
              /*요청하신 화면이 존재하지 않습니다. <br/> 주소가 변경되었거나 삭제된 화면입니다.*/
              <Trans t={t} i18nKey="ErrorComponent.005" components={[<br key="0" />]} />
            ) : (
              /*서비스 이용에 불편을 드려 죄송합니다.<br /> 잠시 후 다시 시도해 주세요.*/
              <Trans t={t} i18nKey="ErrorComponent.006" components={[<br key="0" />]} />
            )}
          </ErrorCard>
        </div>
        <BottomButtons
          buttons={[
            {
              label: buttonText,
              onClick: () => {
                handleClickConfirm();
              },
              Variants: 'primary',
              disabled: false,
            },
          ]}
        />
      </>
    );
  };

  //status error
  if (errorCode === '404' || errorCode === '500') {
    if (props.viewType === 'Page') {
      return (
        <ContentInner className="page-content pb-16">
          <StatusErrorComponent type={'Page'} />
        </ContentInner>
      );
    }

    return (
      <div className="error-container">
        <StatusErrorComponent type={'Component'} />
      </div>
    );
  }

  //그외 업무에러
  if (props.viewType === 'Page') {
    return (
      <ContentInner className="page-content pb-16">
        <NormalErrorComponent type={'Page'} />
      </ContentInner>
    );
  }

  return (
    <div className="error-container">
      <NormalErrorComponent
        type={'Component'}
        classNameProps={errClassName}
        desktopClassName={desktopClassName}
        errButtonsProps={errButtons}
      />
    </div>
  );
});
